/**
 * Message Filter Utility
 * 
 * This utility provides functions to filter and sanitize message content to prevent:
 * 1. Sharing of contact information (phone numbers, emails)
 * 2. Use of profanity or inappropriate language
 * 3. Attempts to bypass the platform's communication channels
 * 4. Other security risks in messaging
 */

/**
 * Regular expressions for detecting various patterns
 */
const patterns = {
  // Phone number patterns (various formats)
  phoneNumbers: [
    /(\+\d{1,3}[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}/g, // Standard formats: +1 123-456-7890, (123) 456-7890, 123.456.7890
    /\d{11}/g, // 11-digit numbers (common for international numbers without formatting)
    /\b\d{10}\b/g, // 10-digit numbers without separators
    /\b\d{3}[\s.-]?\d{3}[\s.-]?\d{4}\b/g, // Common US format
    /\b\d{4}[\s.-]?\d{3}[\s.-]?\d{3}\b/g, // Common international format
    /\b\d{2}[\s.-]?\d{3}[\s.-]?\d{2}[\s.-]?\d{2}\b/g, // European format
    /\b\d{4}[\s.-]?\d{4}\b/g, // Short 8-digit format (common in some countries)
    /\b\d{3}[\s.-]?\d{4}\b/g, // Last 7 digits of phone number
    /\bone[\s.-]?\d{3}[\s.-]?\d{3}[\s.-]?\d{4}\b/gi, // Written format like "one-two-three-four-five-six-seven"
    /\bzero[\s.-]?\d{3}[\s.-]?\d{3}[\s.-]?\d{3,4}\b/gi, // International format with "zero" prefix
  ],
  
  // Email patterns
  emails: [
    /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, // Standard email format
    /\b[A-Za-z0-9._%+-]+\s*[\[@]\s*[A-Za-z0-9.-]+\s*[\].]\s*[A-Z|a-z]{2,}\b/g, // Obfuscated emails (user at domain dot com)
    /\b[A-Za-z0-9._%+-]+\s*\(at\)\s*[A-Za-z0-9.-]+\s*\(dot\)\s*[A-Z|a-z]{2,}\b/gi, // (at) and (dot) format
    /\b[A-Za-z0-9._%+-]+\s*\[at\]\s*[A-Za-z0-9.-]+\s*\[dot\]\s*[A-Z|a-z]{2,}\b/gi, // [at] and [dot] format
    /\b[A-Za-z0-9._%+-]+\s*-at-\s*[A-Za-z0-9.-]+\s*-dot-\s*[A-Z|a-z]{2,}\b/gi, // -at- and -dot- format
    /\b[A-Za-z0-9._%+-]+\s*\{at\}\s*[A-Za-z0-9.-]+\s*\{dot\}\s*[A-Z|a-z]{2,}\b/gi, // {at} and {dot} format
  ],
  
  // Social media handles and URLs
  socialMedia: [
    /\b(instagram|facebook|twitter|linkedin|tiktok|whatsapp|telegram|signal|snapchat|youtube|pinterest|reddit|discord|twitch|threads)\.[a-z]+\/[A-Za-z0-9_.]+\b/gi,
    /\b(instagram|facebook|twitter|linkedin|tiktok|whatsapp|telegram|signal|snapchat|youtube|pinterest|reddit|discord|twitch|threads)\.com/gi,
    /\b(ig|fb|tw|li|tt|wa|tg|sc|yt|pin|rd|dc)\.[a-z]+\/[A-Za-z0-9_.]+\b/gi,
    /\b@[A-Za-z0-9_.]{3,30}\b/g, // Common social media handle format
    /\bfb\.me\/[A-Za-z0-9_.]+\b/gi, // Facebook shortened URLs
    /\bt\.me\/[A-Za-z0-9_.]+\b/gi, // Telegram shortened URLs
    /\bwa\.me\/[A-Za-z0-9_.]+\b/gi, // WhatsApp shortened URLs
    /\bdiscord\.(gg|com\/invite)\/[A-Za-z0-9_.]+\b/gi, // Discord invite links
  ],
  
  // URLs and websites
  urls: [
    /\b(https?:\/\/)?[A-Za-z0-9]+([\-\.]{1}[A-Za-z0-9]+)*\.[A-Za-z]{2,63}(:[0-9]{1,5})?(\/[^\s]*)?\b/gi,
    /\b(www\.)[A-Za-z0-9]+([\-\.]{1}[A-Za-z0-9]+)*\.[A-Za-z]{2,63}\b/gi, // www. prefix without protocol
    /\b[A-Za-z0-9]+([\-\.]{1}[A-Za-z0-9]+)*\.(com|net|org|io|co|me|app|dev|ai)\b/gi, // Common TLDs without www or protocol
    /\bbit\.ly\/[A-Za-z0-9]+\b/gi, // Bit.ly shortened URLs
    /\btinyurl\.com\/[A-Za-z0-9]+\b/gi, // TinyURL shortened URLs
    /\bgoo\.gl\/[A-Za-z0-9]+\b/gi, // Google shortened URLs
  ],
  
  // Platform bypass attempts
  bypassAttempts: [
    /\b(contact|message|dm|pm|email|call|text|phone|reach|chat|ping|connect)\s+(me|us|you|outside|elsewhere|direct|privately|later|offline)\b/gi,
    /\b(let's|lets|can|could|should|would|will)\s+(talk|chat|discuss|connect|meet|sync|catch up)\s+(outside|elsewhere|direct|privately|off|platform|offline|later)\b/gi,
    /\b(off|outside|beyond)\s+(the|this)\s+(platform|site|app|website|chat|system)\b/gi,
    /\b(my|your)\s+(contact|email|phone|number|details|info|telegram|whatsapp|signal)\b/gi,
    /\b(send|give|share|pm|dm)\s+(me|you|your|my)\s+(details|info|contact|number)\b/gi,
    /\b(reach|contact|find)\s+(me|you)\s+(at|on|via|through|using)\b/gi,
    /\b(take|move|continue)\s+(this|our|the)\s+(conversation|discussion|chat|talk)\s+(elsewhere|outside|offline|private)\b/gi,
    /\b(i'll|ill|i will|we can)\s+(send|text|call|email|message|dm)\s+(you|u|ya|thee)\b/gi,
  ],
};

// Common profanity words - this is a minimal list, should be expanded
const profanityList = [
  'ass', 'asshole', 'bitch', 'bastard', 'cunt', 'damn', 'dick', 'fuck', 'fucking', 
  'shit', 'piss', 'pussy', 'slut', 'whore', 'cock', 'penis', 'vagina', 'nigger', 'nigga'
];

// Create a regex pattern for profanity with word boundaries
const profanityPattern = new RegExp(`\\b(${profanityList.join('|')})\\b`, 'gi');

/**
 * Check if a message contains any filtered content
 * @param {string} message - The message to check
 * @returns {object} - Object with detection results and details
 */
export const detectFilteredContent = (message) => {
  if (!message || typeof message !== 'string') {
    return { isClean: true, detections: [] };
  }
  
  const detections = [];
  
  // Check for phone numbers
  for (const pattern of patterns.phoneNumbers) {
    const matches = message.match(pattern);
    if (matches) {
      detections.push({
        type: 'phone_number',
        matches: [...new Set(matches)], // Remove duplicates
      });
    }
  }
  
  // Check for emails
  for (const pattern of patterns.emails) {
    const matches = message.match(pattern);
    if (matches) {
      detections.push({
        type: 'email',
        matches: [...new Set(matches)],
      });
    }
  }
  
  // Check for social media
  for (const pattern of patterns.socialMedia) {
    const matches = message.match(pattern);
    if (matches) {
      detections.push({
        type: 'social_media',
        matches: [...new Set(matches)],
      });
    }
  }
  
  // Check for URLs
  for (const pattern of patterns.urls) {
    const matches = message.match(pattern);
    if (matches) {
      detections.push({
        type: 'url',
        matches: [...new Set(matches)],
      });
    }
  }
  
  // Check for bypass attempts
  for (const pattern of patterns.bypassAttempts) {
    const matches = message.match(pattern);
    if (matches) {
      detections.push({
        type: 'bypass_attempt',
        matches: [...new Set(matches)],
      });
    }
  }
  
  // Check for profanity
  const profanityMatches = message.match(profanityPattern);
  if (profanityMatches) {
    detections.push({
      type: 'profanity',
      matches: [...new Set(profanityMatches)],
    });
  }
  
  return {
    isClean: detections.length === 0,
    detections,
  };
};

/**
 * Generate a user-friendly error message based on detected content
 * @param {object} detectionResult - Result from detectFilteredContent
 * @returns {string} - User-friendly error message
 */
export const getFilterErrorMessage = (detectionResult) => {
  if (detectionResult.isClean) {
    return null;
  }
  
  const messages = {
    phone_number: 'Phone numbers are not allowed for security reasons',
    email: 'Email addresses are not allowed for security reasons',
    social_media: 'Social media handles or links are not allowed',
    url: 'URLs or website links are not allowed',
    bypass_attempt: 'Attempts to communicate outside the platform are not allowed',
    profanity: 'Inappropriate language is not allowed',
  };
  
  // Get unique detection types
  const detectionTypes = [...new Set(detectionResult.detections.map(d => d.type))];
  
  // Return appropriate message(s)
  if (detectionTypes.length === 1) {
    return messages[detectionTypes[0]];
  } else {
    return 'Your message contains content that is not allowed (contact information, inappropriate language, or attempts to bypass the platform)';
  }
};

/**
 * Sanitize a message by replacing filtered content with asterisks
 * Not currently used but could be useful for partial filtering
 * @param {string} message - The message to sanitize
 * @returns {string} - Sanitized message
 */
export const sanitizeMessage = (message) => {
  if (!message || typeof message !== 'string') {
    return message;
  }
  
  let sanitized = message;
  
  // Replace phone numbers
  for (const pattern of patterns.phoneNumbers) {
    sanitized = sanitized.replace(pattern, match => '*'.repeat(match.length));
  }
  
  // Replace emails
  for (const pattern of patterns.emails) {
    sanitized = sanitized.replace(pattern, match => '*'.repeat(match.length));
  }
  
  // Replace social media
  for (const pattern of patterns.socialMedia) {
    sanitized = sanitized.replace(pattern, match => '*'.repeat(match.length));
  }
  
  // Replace URLs
  for (const pattern of patterns.urls) {
    sanitized = sanitized.replace(pattern, match => '*'.repeat(match.length));
  }
  
  // Replace profanity
  sanitized = sanitized.replace(profanityPattern, match => '*'.repeat(match.length));
  
  return sanitized;
};

export default {
  detectFilteredContent,
  getFilterErrorMessage,
  sanitizeMessage,
};
